import { authenticatedApi } from '@/lib/api/core';
import {
  ConfirmUploadResponse,
  ConsultationVideo,
  ConsultationVideoListParams,
  ConsultationVideoPayload,
  ConsultationVideoUpdatePayload,
  MultipartCompleteRequest,
  MultipartCompleteResponse,
  MultipartInitiateRequest,
  MultipartInitiateResponse,
  PaginatedResponse,
  PlaybackResponse,
  PresignedUploadRequest,
  PresignedUploadResponse,
  VideoPlaylist,
  VideoPlaylistItem,
  VideoPlaylistItemPayload,
  VideoPlaylistPayload,
  VideoRatingPayload,
  VideoRatingRecord,
  VideoRatingUpdatePayload,
} from './types';
import { asPaginatedResponse } from './utils';

const STORAGE_BASE = '/storage/files';
const CLINICAL_BASE = '/clinical/consultation-videos';
const RATINGS_BASE = '/clinical/consultation-video-ratings';
const PLAYLISTS_BASE = '/clinical/consultation-video-playlists';
const PLAYLIST_ITEMS_BASE = '/clinical/consultation-video-playlist-items';

export const requestPresignedUpload = async (
  payload: PresignedUploadRequest
): Promise<PresignedUploadResponse> => {
  const response = await authenticatedApi.post<PresignedUploadResponse>(
    `${STORAGE_BASE}/presigned-upload`,
    payload
  );
  return response.data;
};

export const uploadToS3 = (
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const contentType = file.type || 'video/mp4';
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');

    xhr.upload.onprogress = (event) => {
      if (!onProgress || !event.lengthComputable) {
        return;
      }
      const percent = Math.round((event.loaded / event.total) * 100);
      onProgress(percent);
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
};

export const createConsultationVideo = async (
  payload: ConsultationVideoPayload
): Promise<ConsultationVideo> => {
  const response = await authenticatedApi.post<ConsultationVideo>(CLINICAL_BASE, payload);
  return response.data;
};

export const listConsultationVideos = async (
  params?: ConsultationVideoListParams,
  signal?: AbortSignal
): Promise<PaginatedResponse<ConsultationVideo>> => {
  const response = await authenticatedApi.get<
    PaginatedResponse<ConsultationVideo> | ConsultationVideo[]
  >(CLINICAL_BASE, { params, signal });
  return asPaginatedResponse(response.data);
};

export const getConsultationVideo = async (videoId: string): Promise<ConsultationVideo> => {
  const response = await authenticatedApi.get<ConsultationVideo>(`${CLINICAL_BASE}/${videoId}`);
  return response.data;
};

export const updateConsultationVideo = async (
  videoId: string,
  payload: ConsultationVideoUpdatePayload
): Promise<ConsultationVideo> => {
  const response = await authenticatedApi.patch<ConsultationVideo>(
    `${CLINICAL_BASE}/${videoId}`,
    payload
  );
  return response.data;
};

export const deleteConsultationVideo = async (videoId: string): Promise<void> => {
  await authenticatedApi.delete(`${CLINICAL_BASE}/${videoId}`);
};

export const getPlaybackUrl = async (videoId: string): Promise<PlaybackResponse> => {
  const response = await authenticatedApi.get<PlaybackResponse>(
    `${CLINICAL_BASE}/${videoId}/playback`
  );
  return response.data;
};

export const listVideoRatings = async (
  videoId: string,
  signal?: AbortSignal
): Promise<PaginatedResponse<VideoRatingRecord> | VideoRatingRecord[]> => {
  const response = await authenticatedApi.get<
    PaginatedResponse<VideoRatingRecord> | VideoRatingRecord[]
  >(RATINGS_BASE, { params: { video_id: videoId }, signal });
  return response.data;
};

export const createVideoRating = async (
  payload: VideoRatingPayload
): Promise<VideoRatingRecord> => {
  const response = await authenticatedApi.post<VideoRatingRecord>(RATINGS_BASE, payload);
  return response.data;
};

export const updateVideoRating = async (
  ratingId: string,
  payload: VideoRatingUpdatePayload
): Promise<VideoRatingRecord> => {
  const response = await authenticatedApi.patch<VideoRatingRecord>(
    `${RATINGS_BASE}/${ratingId}`,
    payload
  );
  return response.data;
};

export const createPlaylist = async (payload: VideoPlaylistPayload): Promise<VideoPlaylist> => {
  const response = await authenticatedApi.post<VideoPlaylist>(PLAYLISTS_BASE, payload);
  return response.data;
};

export const listPlaylists = async (params?: {
  client_id?: string;
}): Promise<PaginatedResponse<VideoPlaylist> | VideoPlaylist[]> => {
  const response = await authenticatedApi.get<PaginatedResponse<VideoPlaylist> | VideoPlaylist[]>(
    PLAYLISTS_BASE,
    { params }
  );
  return response.data;
};

export const listPlaylistItems = async (
  playlistId: string
): Promise<PaginatedResponse<VideoPlaylistItem> | VideoPlaylistItem[]> => {
  const response = await authenticatedApi.get<
    PaginatedResponse<VideoPlaylistItem> | VideoPlaylistItem[]
  >(`${PLAYLISTS_BASE}/${playlistId}/items`);
  return response.data;
};

export const createPlaylistItem = async (
  payload: VideoPlaylistItemPayload
): Promise<VideoPlaylistItem> => {
  const response = await authenticatedApi.post<VideoPlaylistItem>(PLAYLIST_ITEMS_BASE, payload);
  return response.data;
};

export const deletePlaylistItem = async (itemId: string): Promise<void> => {
  await authenticatedApi.delete(`${PLAYLIST_ITEMS_BASE}/${itemId}`);
};

export const confirmUpload = async (fileId: string): Promise<ConfirmUploadResponse> => {
  const response = await authenticatedApi.post<ConfirmUploadResponse>(
    `${STORAGE_BASE}/${fileId}/confirm-upload`
  );
  return response.data;
};

export const initiateMultipartUpload = async (
  payload: MultipartInitiateRequest
): Promise<MultipartInitiateResponse> => {
  const response = await authenticatedApi.post<MultipartInitiateResponse>(
    `${STORAGE_BASE}/multipart-upload/initiate`,
    payload
  );
  return response.data;
};

export const uploadPart = (
  url: string,
  chunk: Blob,
  onProgress?: (loaded: number, total: number) => void
): Promise<{ partNumber: number; etag: string }> => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);

    xhr.upload.onprogress = (event) => {
      if (onProgress && event.lengthComputable) {
        onProgress(event.loaded, event.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader('ETag')?.replace(/"/g, '') ?? '';
        resolve({ partNumber: 0, etag });
      } else {
        reject(new Error(`Part upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error('Part upload failed'));
    xhr.send(chunk);
  });
};

export const completeMultipartUpload = async (
  payload: MultipartCompleteRequest
): Promise<MultipartCompleteResponse> => {
  const response = await authenticatedApi.post<MultipartCompleteResponse>(
    `${STORAGE_BASE}/multipart-upload/complete`,
    payload
  );
  return response.data;
};

export const abortMultipartUpload = async (payload: {
  file_id: string;
  upload_id: string;
  key: string;
}): Promise<void> => {
  await authenticatedApi.post(`${STORAGE_BASE}/multipart-upload/abort`, payload);
};
